import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import {
   BankAccount,
   OTP_ACTION,
   Participations,
   User,
   UserType,
} from '@app/models';
import { Router } from '@angular/router';

import { forkJoin, Observable, of, throwError, BehaviorSubject } from 'rxjs';
import {
   catchError,
   map,
   shareReplay,
   switchMap,
   take,
   tap,
   timeout,
   retry,
} from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { environment } from 'src/environments/environment';
import {
   UserBeneficiary,
   OTP_METHOD,
   UserOptions,
   UserReferencedRes,
   UserRegisterIneRes,
   OtpMethodRes,
   UserInfo,
   UserInfoAddional,
   UserPartMove,
   UserSettings,
   UserWalletMove,
   UserTaxWithHoldingTransactions,
   UserPartsSolding,
   UserPartsPublished,
   DevelopmentPerProject,
   DevelopmentCalendarPayments,
} from '@interfaces/app';
import {
   HEADER_RESET_REQ,
   HEADER_DONT_SAVE,
   HEADER_DONT_SAVE_REQ,
   HEADER_RESET,
} from '@app/constants';
import { Utils } from '../utils/utils.service';
import { GpsV2Service } from '../gps-v2/gps-v2.service';
import { ValidationType } from '@app/interfaces';

@Injectable({
   providedIn: 'root'
})
export class UserService {
   public userInfo = new BehaviorSubject<UserInfo | undefined>(undefined);
   public userDetail = new BehaviorSubject<UserInfoAddional>(undefined);
   public userParticipations = new BehaviorSubject<Participations[]>(undefined);

   public buyParticipationsData: { devId: string; devName: string; amount: number; partValue: number; devPerformance: number; };
   public withdrawData: { bankAccount: BankAccount; amount: number; pwd?: string; };

   public userTypeIsDev = new BehaviorSubject<boolean>(false);
   public userNotificationsChange = new BehaviorSubject<any>([]);

   cacheImg: { [prop: string]: SafeUrl } = {};
   // eslint-disable-next-line @typescript-eslint/ban-types
   cacheUser: { [prop: string]: {} } = {};
   otpMethod: OTP_METHOD;
   /** Variable para indicar si el usuario logueado ya se cargó */
   userOnLoad = false;

   // Variables para manejar peticiones simultáneas de getUserDetail
   private userDetailInProgress: Observable<User> | null = null;
   private userDetailLastRequestTime: number = 0;
   private userDetailCacheData: User | null = null;
   private userDetailCacheTime: number = 0;
   private readonly CACHE_DURATION = 400; // 400ms de caché
   private readonly REQUEST_DELAY = 50; // 50ms de delay entre peticiones

   // Variables para manejar peticiones simultáneas (estrategia similar a loadUser)
   private userInfoAdditionalOnLoad = false;
   private userSettingsOnLoad = false;
   private bankAccountsOnLoad = false;
   private referenceOnLoad = false;
   private readonly TIMER_DELAY = 150; // 150ms como en timerGetUser

   // Variable para rastrear el tiempo de la última petición de loadUser
   private loadUserLastRequestTime: number = 0;
   private loadUserInProgress: Observable<UserInfo> | null = null;

   get userInfo$(): Observable<UserInfo | undefined> {
      return this.userInfo.asObservable();
   }

   constructor(
      private readonly http: HttpClient,
      private readonly gpsService: GpsV2Service,
      private readonly toastr: ToastrService,
      private readonly router: Router,
      private readonly utils: Utils
   ) { }

   tokenIsActive() {
      const exp = sessionStorage.getItem('EXP_AUTH');

      return exp ? Number(exp) > Date.now() : false;
   }

   /** @deprecated */
   getUserType() {
      return this.http.post<{ t: UserType }>(`${environment.apiUrlBase}/user/tp`, {}).pipe(
         shareReplay(),
         map(r => {
            const t = r.t === 'd';
            this.userTypeIsDev.next(t);
            return r.t;
         }));
   }

   /** @deprecated */
   getUserInfo() {
      return this.getUserType().pipe(
         shareReplay(),
         switchMap((isDeveloper) => isDeveloper === 'd' ? this.getUserDeveloper() : this.getUser()));
   }

   getOptionalUser(): Observable<UserInfo> {
      return this.tokenIsActive() ? this.getUserInf() : of(null);
   }

   /** @deprecated */
   getUser() {
      return this.http.post<User>(`${environment.apiUrlBase}/user`, {}, this.cacheUser.getUserInfo || HEADER_RESET)
         .pipe(tap((w) => { this.cacheUser.getUserInfo = {} }))
   }

   /** Función para obtener información del usuario y puede ser ejecutado más de 1 vez entre shell con otro componente y evitar más de 1 request */
   getUserInf(forceReload?: boolean): Observable<UserInfo | /*DeveloperInfo*/ any> {
      if (forceReload) {
         return this.loadUser(true);
      } else if (this.cacheUser.getUserInf) {
         return this.loadUser();
      } else if (!this.cacheUser.getUserInf && !this.userOnLoad) {
         this.userOnLoad = true;
         return this.loadUser();
      } else {
         return this.timerGetUser();
      }
   }

   /** Revisar estatus actual de stp cuando crean wallet */
   checkWalletStatus() {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', (Math.random() * 300).toPrecision());

      return this.http.get<{ stateSTP: string; balance: number }>(`${environment.apiUrlBase}/wallet/status`, { headers })
   }

   /** Tiempo de espera si ya fue solicitado la información dle usuario */
   timerGetUser() {
      return new Observable(res => { setTimeout(() => { res.next(); res.complete() }, 150) }).pipe(switchMap(() => this.getUserInf()))
   }

   /** Cargar información del usuario */
   private loadUser(forceReload = false): Observable<UserInfo> {
      const now = Date.now();
      const timeSinceLastRequest = now - this.loadUserLastRequestTime;

      // Si hay una petición en progreso y ha sido hace menos de 400ms, retornarla
      if (this.loadUserInProgress && timeSinceLastRequest < this.CACHE_DURATION) {
         return this.loadUserInProgress;
      }

      // Si forceReload está activo pero la última petición fue hace menos de 400ms
      if (forceReload && timeSinceLastRequest < this.CACHE_DURATION) {
         // Cambiar forceReload a false para usar caché si está disponible
         forceReload = false;
      }

      const headers = forceReload ? HEADER_RESET_REQ : (this.cacheUser.getUserInf || HEADER_RESET_REQ);

      // Si se fuerza la recarga, limpiar el caché existente
      if (forceReload) {
         this.cacheUser.getUserInf = undefined;
      }

      // Actualizar timestamp de última petición
      this.loadUserLastRequestTime = now;
      this.loadUserInProgress = this.http.get<UserInfo>(`${environment.apiUrlBase}/user/info`, { headers }).pipe(
         take(1),
         tap((user) => {
            const endTime = Date.now();
            this.userInfo.next(user);
            this.cacheUser.getUserInf = { };
            this.otpMethod = user.otpMethod;
            this.userOnLoad = false;
            // Limpiar referencia a petición en progreso después de un delay
            setTimeout(() => {
               this.loadUserInProgress = null;
            }, 50);
         }),
         catchError((error) => {
            this.userOnLoad = false;
            this.loadUserInProgress = null;
            if (error.status === 401) {
               this.toastr.warning('Sesión expirada. Por favor, inicia sesión nuevamente.');
               return of(null);
            }
            return throwError(error);
         }),
         shareReplay(1) // Compartir la respuesta entre múltiples suscriptores
      );

      return this.loadUserInProgress;
   }

   /**
    * Método conveniente para obtener información básica y adicional del usuario en una sola llamada
    * Útil para componentes que necesitan ambos conjuntos de datos
    */
   getUserFullInfo(forceReload?: boolean): Observable<{ basic: UserInfo; additional: UserInfoAddional }> {
      return forkJoin({
         basic: this.getUserInf(forceReload),
         additional: this.getUserInfoAdditional(forceReload)
      }).pipe(
         catchError((error) => {
            return throwError(error);
         })
      );
   }

   /** Obtener información adicional de usuario como su dirección, rfc, curp, validaciones */
   getUserInfoAdditional(forceReload?: boolean): Observable<UserInfoAddional> {
      if (forceReload || this.cacheUser.getUserInfoAdd === undefined || this.cacheUser.getUserInfoAdd === null) {
         return this.loadUserInfoAdditional(true);
      } else if (this.cacheUser.getUserInfoAdd) {
         return this.loadUserInfoAdditional();
      } else if (!this.cacheUser.getUserInfoAdd && !this.userInfoAdditionalOnLoad) {
         this.userInfoAdditionalOnLoad = true;
         return this.loadUserInfoAdditional();
      } else {
         return this.timerGetUserInfoAdditional();
      }
   }

   /** Tiempo de espera si ya fue solicitada la información adicional del usuario */
   private timerGetUserInfoAdditional(): Observable<UserInfoAddional> {
      return new Observable(res => {
         setTimeout(() => {
            res.next();
            res.complete()
         }, this.TIMER_DELAY)
      }).pipe(switchMap(() => this.getUserInfoAdditional()));
   }

   /** Cargar información adicional del usuario */
   private loadUserInfoAdditional(forceReload = false): Observable<UserInfoAddional> {
      const headers = forceReload ? HEADER_RESET_REQ : (this.cacheUser.getUserInfoAdd || HEADER_RESET_REQ);
      return this.http.get<UserInfoAddional>(`${environment.apiUrlBase}/user/info/additional`, { headers }).pipe(
         take(1),
         catchError((error) => {
            this.userInfoAdditionalOnLoad = false;
            return throwError(error);
         })
      );
   }

   /** Obtener configuraciones del usuario */
   getUserSettings(forceReload?: boolean): Observable<UserSettings> {
      if (forceReload) {
         return this.loadUserSettings(true);
      } else if (this.cacheUser.getUserSettings) {
         return this.loadUserSettings();
      } else if (!this.cacheUser.getUserSettings && !this.userSettingsOnLoad) {
         this.userSettingsOnLoad = true;
         return this.loadUserSettings();
      } else {
         return this.timerGetUserSettings();
      }
   }

   /** Tiempo de espera si ya fue solicitada la configuración del usuario */
   private timerGetUserSettings(): Observable<UserSettings> {
      return new Observable(res => {
         setTimeout(() => {
            res.next();
            res.complete()
         }, this.TIMER_DELAY)
      }).pipe(switchMap(() => this.getUserSettings()));
   }

   /** Cargar configuraciones del usuario */
   private loadUserSettings(forceReload = false): Observable<UserSettings> {
      const headers = forceReload ? HEADER_RESET_REQ : (this.cacheUser.getUserSettings || HEADER_RESET_REQ);

      return this.http.get<UserSettings>(`${environment.apiUrlBase}/user/info/settings`, { headers }).pipe(
         take(1),
         tap((response) => {
            this.cacheUser.getUserSettings = {};
            this.userSettingsOnLoad = false;
         }),
         catchError((error) => {
            this.userSettingsOnLoad = false;
            return throwError(error);
         })
      );
   }

   /** Obtener histórico de movimientos del usuario */
   getUserHistoric(forceRefresh: boolean = false) {
      const walletHeaders = forceRefresh ? HEADER_RESET_REQ : (this.cacheUser.getUserHistoricWallet || HEADER_RESET_REQ);
      const partsHeaders = forceRefresh ? HEADER_RESET_REQ : (this.cacheUser.getUserHistoricParts || HEADER_RESET_REQ);
      const taxHeaders = forceRefresh ? HEADER_RESET_REQ : (this.cacheUser.getUserHistoricParts || HEADER_RESET_REQ);

      return forkJoin({
        user: this.getUserInf(),
        wallet: this.http.get<UserWalletMove[]>(`${environment.apiUrlBase}/user/info/wallet`, { headers: walletHeaders }),
        parts: this.http.get<UserPartMove[]>(`${environment.apiUrlBase}/user/info/parts`, { headers: partsHeaders }),
        taxWithHolding: this.http.get<UserTaxWithHoldingTransactions[]>(`${environment.apiUrlBase}/user/info/wallet-tax-withholding`, { headers: taxHeaders }),
      }).pipe(
        tap(() => {
          this.cacheUser.getUserHistoricWallet = {};
          this.cacheUser.getUserHistoricParts = {};
          this.cacheUser.taxWithHolding = {};
        }),
      );
    }


   getUserPartsPerformance() {
      return this.http.get<any[]>(`${environment.apiUrlBase}/user/info/parts-performance`, { headers: this.cacheUser.getPartsPerformance || HEADER_RESET_REQ })
        .pipe(
          map(data => data.map(item => this.transformUserPartData(item))),
          map(data => this.sortByExpectedDate(data)),
          tap(() => { this.cacheUser.getPartsPerformance = {}; }),
        );
   }

   private transformUserPartData(item: any): any {
      let distAmount: string | number = this.calculateDistAmount(item);
      const nextDistDate = this.utils.calculateNextDistributionDate(item.lastDistDate || item.firstDistDate, item.devCapitalization);

      if(distAmount === 0 && item?.performanceDistType === 'fixed') {
         distAmount = 'Planificación inicial'
      } else if (distAmount === 0) {
         distAmount = 'Sujeto a resultados'
      }


      return {
        devName: item.devName,
        performanceDistType: this.getPerformanceDistType(item?.performanceDistType || item?.performanceType),
        distAmount: distAmount,
        status: 'Pendiente',
        expectedDate: nextDistDate,
        paymentDate: item?.paymentDate,
        amountTotal: (item?.performance + item?.principal),
        inFunding: item.inFunding
      };
    }

    private calculateDistAmount(item: any): number {
      return item.performanceDistType === 'fixed'
        ? parseFloat((item.averageDistAmountPerPart * item.amount).toFixed(2))
        : 0;
    }

    private getPerformanceDistType(performanceDistType: string): string {
      return performanceDistType === 'fixed' ? 'Fijo' : 'Variable';
    }

    private sortByExpectedDate(data: any[]): any[] {
      return data.sort((a, b) => {
        const dateA = new Date(a.expectedDate);
        const dateB = new Date(b.expectedDate);

        if (dateA > dateB) return 1;
        if (dateA < dateB) return -1;
        return 0;
      });
    }

   downloadStatement(year: number, month: number, nativePlatform?: boolean) {
      const queryParam = nativePlatform ? '?nativePlatform=true' : '?nativePlatform=false';
      return this.http.get(`${environment.apiUrlBase}/user/file/statement/${year}/${month}${queryParam}`, { responseType: 'arraybuffer', headers: HEADER_DONT_SAVE_REQ });
   }

   getReference(forceReload?: boolean): Observable<UserReferencedRes> {
      if (forceReload) {
         return this.loadReference(true);
      } else if (this.cacheUser.getRefs) {
         return this.loadReference();
      } else if (!this.cacheUser.getRefs && !this.referenceOnLoad) {
         this.referenceOnLoad = true;
         return this.loadReference();
      } else {
         return this.timerGetReference();
      }
   }

   /** Tiempo de espera si ya fue solicitada la información de referencias */
   private timerGetReference(): Observable<UserReferencedRes> {
      return new Observable(res => {
         setTimeout(() => {
            res.next();
            res.complete()
         }, this.TIMER_DELAY)
      }).pipe(switchMap(() => this.getReference()));
   }

   /** Cargar información de referencias del usuario */
   private loadReference(forceReload = false): Observable<UserReferencedRes> {
      const headers = forceReload ? HEADER_RESET_REQ : (this.cacheUser.getRefs || HEADER_RESET_REQ);
      return this.http.get<UserReferencedRes>(`${environment.apiUrlBase}/user/info/refs`, { headers }).pipe(
         take(1),
         tap((response) => {
            this.cacheUser.getRefs = {};
            this.referenceOnLoad = false;
         }),
         catchError((error) => {
            this.referenceOnLoad = false;
            return throwError(error);
         })
      );
   }

   getUserDeveloper() {
      return this.http.post<any>(`${environment.apiUrlBase}/developer`, {}, this.cacheUser.getUserInfo || HEADER_RESET)
         .pipe(tap(() => this.cacheUser.getUserInfo = {}));
   }

   getUserDetail(): Observable<User> {
      const now = Date.now();

      if (this.userDetailCacheData && (now - this.userDetailCacheTime) < this.CACHE_DURATION) {
         return of(this.userDetailCacheData);
      }

      // Si hay una petición en curso, retornarla
      if (this.userDetailInProgress) {
         return this.userDetailInProgress;
      }

      // Si la última petición fue hace muy poco, agregar un pequeño delay
      const timeSinceLastRequest = now - this.userDetailLastRequestTime;
      if (timeSinceLastRequest < this.REQUEST_DELAY) {
         const delayTime = this.REQUEST_DELAY - timeSinceLastRequest;
         return new Observable<User>(observer => {
            setTimeout(() => {
               this.getUserDetail().subscribe({
                  next: (data) => {
                     observer.next(data);
                     observer.complete();
                  },
                  error: (err) => {
                     observer.error(err);
                  }
               });
            }, delayTime);
         });
      }

      // Actualizar timestamp de última petición
      this.userDetailLastRequestTime = now;

      this.userDetailInProgress = this.http.post<User>(
         `${environment.apiUrlBase}/user/info`,
         {},
         this.cacheUser.getUserDetail || HEADER_RESET
      ).pipe(
         take(1),
         tap((response) => {
            const endTime = Date.now();
            // Guardar en caché
            this.userDetailCacheData = response;
            this.userDetailCacheTime = endTime;

            // Limpiar caché del header
            this.cacheUser.getUserDetail = {};

            // Limpiar referencia a petición en progreso
            this.userDetailInProgress = null;
         }),
         catchError((error) => {
            // Limpiar referencia a petición en progreso
            this.userDetailInProgress = null;

            if (error.status === 401) {
               this.toastr.warning('Sesión expirada. Por favor, inicia sesión nuevamente.');
               return of(null);
            }

            // Re-lanzar el error
            return throwError(error);
         }),
         // Compartir la misma petición entre todos los suscriptores
         shareReplay(1)
      );

      return this.userDetailInProgress;
   }

   getUserPartMoves() {
      return this.http.post<any[]>(`${environment.apiUrlBase}/user/part-moves`, {}, this.cacheUser.userPatMoves || HEADER_RESET)
         .pipe(tap(() => this.cacheUser.userPatMoves = {}));
   }

   getUserSignature(reset?: boolean) {
      return this.http.get<{ url: string, createAt: number }>(`${environment.apiUrlBase}/user/signature`, reset ? this.cacheUser.userOptions || HEADER_RESET : HEADER_RESET)
         .pipe(tap(() => this.cacheUser.userOptions = {}));
   }

   getUserPendingSTP() {
      return this.http.post<{ stateSTP: string; }>(`${environment.apiUrlBase}/wallet/pd`, {}, HEADER_DONT_SAVE)
   }

   getUserOptions() {
      return this.http.get<UserOptions>(`${environment.apiUrlBase}/user/options`);
   }

   editUserInfo(user: any, reqUpdateToken?: string): Observable<any> {
      const gps = this.gpsService.getCoordinatesFromLocalStorage();

      const config = { headers: reqUpdateToken ? HEADER_DONT_SAVE_REQ.set('tk', reqUpdateToken) : HEADER_DONT_SAVE_REQ };
      const service = reqUpdateToken ? 'user/pd/edit' : 'user/edit';

      const sendRequest = (): Observable<any> => {
        return this.http.post(`${environment.apiUrlBase}/${service}`, { user }, config).pipe(
          map(u => {
            this.cacheUser.getUserDetail = undefined;
            // Limpiar también el caché de getUserDetail
            this.clearUserDetailCache();
            return u;
          })
        );
      };

      if (gps) {
        return sendRequest();
      } else {
        return this.gpsService.defineGPS().pipe(
          switchMap((success) => {
            if (success) {
              return sendRequest();
            } else {
              return of(false);
            }
          })
        );
      }
    }

   editReqUserInfo(user: any): Observable<any> {
      const coords = this.gpsService.getCoordinatesFromLocalStorage();

      const sendRequest = (): Observable<any> => {
        return this.http.post(`${environment.apiUrlBase}/user/pd/fl`, { user }, HEADER_DONT_SAVE).pipe(
          map((u) => {
            this.cacheUser.getUserDetail = undefined;
            // Limpiar también el caché de getUserDetail
            this.clearUserDetailCache();
            return u;
          })
        );
      };

      if (coords) {
        return sendRequest();
      } else {
        return this.gpsService.defineGPS().pipe(
          switchMap((success) => {
            if (success) {
              const latestCoords = this.gpsService.getCoordinatesFromLocalStorage();
              if (latestCoords) {
                return sendRequest();
              } else {
                return throwError(() => new Error('Coordenadas no disponibles tras GPS.'));
              }
            } else {
              return of(false);
            }
          })
        );
      }
   }

   getBinnacle() {
      return this.http.post<any[]>(`${environment.apiUrlBase}/user/balance-moves`, {}, this.cacheUser.userBinnacle || HEADER_RESET)
         .pipe(tap(() => this.cacheUser.userBinnacle = {}));
   }

   getBankAccounts(forceReload?: boolean): Observable<any[]> {
      if (forceReload) {
         return this.loadBankAccounts(true);
      } else if (this.cacheUser.bankAccounts) {
         return this.loadBankAccounts();
      } else if (!this.cacheUser.bankAccounts && !this.bankAccountsOnLoad) {
         this.bankAccountsOnLoad = true;
         return this.loadBankAccounts();
      } else {
         return this.timerGetBankAccounts();
      }
   }

   /** Tiempo de espera si ya fue solicitada la información de cuentas bancarias */
   private timerGetBankAccounts(): Observable<any[]> {
      return new Observable(res => {
         setTimeout(() => {
            res.next();
            res.complete()
         }, this.TIMER_DELAY)
      }).pipe(switchMap(() => this.getBankAccounts()));
   }

   /** Cargar cuentas bancarias del usuario */
   private loadBankAccounts(forceReload = false): Observable<any[]> {
      const headers = forceReload ? HEADER_RESET_REQ : (this.cacheUser.bankAccounts || HEADER_RESET_REQ);
      return this.http.get<any[]>(`${environment.apiUrlBase}/user/info/bank-accounts`, { headers }).pipe(
         take(1),
         tap(() => {
            this.cacheUser.bankAccounts = {};
            this.bankAccountsOnLoad = false;
         }),
         catchError((error) => {
            this.bankAccountsOnLoad = false;
            return throwError(error);
         })
      );
   }

   deleteBankAccount(id: string, options: { isNativePlatform: boolean; method: 'auth-app' | 'email' | 'sms'; code?: string }) {
      const { isNativePlatform, method, code } = options;

      let endpoint = '/user/edit/bank-account';
      if (!isNativePlatform) {
        endpoint += method === 'auth-app' ? '/mfa' : '/otp';
      }

      let headers = HEADER_DONT_SAVE_REQ;
      if (!isNativePlatform && code) {
         const encodedCode = method === 'auth-app' ? window.btoa(code) : this.utils.encodeBase64(code);
         headers = headers.set('tk', encodedCode);
      }

      return this.http.delete<any>(`${environment.apiUrlBase}${endpoint}`, { body: { id }, headers }).pipe(tap(() => this.cacheUser.bankAccounts = undefined));
    }


   updateBankAccount(id: string, alias: string) {
      return this.http.put<any>(`${environment.apiUrlBase}/user/edit/bank-account`, { id, alias }, HEADER_DONT_SAVE)
         .pipe(tap(() => this.cacheUser.bankAccounts = undefined));
   }

   uploadSignature(dataURL: string, token?: string) {
      const blobBin = this.utils.base64ToUtf8(dataURL.split(',')[1]);
      const array = [];

      for (let i = 0; i < blobBin.length; i++) {
        array.push(blobBin.charCodeAt(i));
      }

      const blobSignature: any = new Blob([new Uint8Array(array)], { type: 'image/png' });

      blobSignature.lastModifiedDate = new Date();
      blobSignature.name = 'signature';

      const signatureForm = new FormData();

      const gpsRaw = sessionStorage.getItem('GPS_LOCATION');
      let latitude: number, longitude: number, accuracy: number;

      if (gpsRaw) {
        try {
          const [lat, lon, acc] = JSON.parse(gpsRaw).split('|');
          latitude = parseFloat(lat);
          longitude = parseFloat(lon);
          accuracy = parseFloat(acc);
        } catch (e) {
          console.log('Error al parsear coordenadas de GPS desde sessionStorage', e);
        }
      }

      const params = this.utils.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);

      signatureForm.set('signature', blobSignature);
      const headers = HEADER_DONT_SAVE_REQ.set('tk', token).set('pa', params);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/user/up-signature-pld`, signatureForm, { headers });
    }


   /**  */
   uploadImg(file: File, type: string, uploadReqToken?: string) {
      const data = new FormData();
      const params = this.utils.utf8ToBase64(type);
      const config = { headers: uploadReqToken ? HEADER_DONT_SAVE_REQ.set('tk', uploadReqToken) : HEADER_DONT_SAVE_REQ };
      const service = uploadReqToken ? 'user/pd/fl' : 'user/upload/fl';

      data.set('file', file);
      data.set('p', params);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/${service}`, data, config);
   }

   /**  */
   uploadFile(file: File, type: string, uploadReqToken?: string) {
      const data = new FormData();
      const param = this.utils.utf8ToBase64(type);
      const config = { headers: uploadReqToken ? HEADER_DONT_SAVE_REQ.set('tk', uploadReqToken) : HEADER_DONT_SAVE_REQ };
      const service = uploadReqToken ? 'user/pd/up' : 'user/up';

      data.set('file', file);
      data.set('param', param);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/${service}`, data, config);
   }

   getReferencedUser() {
      return this.http.post<{ pid: string; ref: number; }>(`${environment.apiUrlBase}/user/references`, {});
   }

   private otpMethodAnalytics: Record<OTP_ACTION, string> = {
      [OTP_ACTION.SPO]: 'retiro',
      [OTP_ACTION.RESALE]: 'reventa',
      [OTP_ACTION.BUY]: 'compra',
      [OTP_ACTION.ACCEPT_RESOURCES]: 'aceptar_recursos',
      [OTP_ACTION.SET_ON_SALE]: 'poner_venta',
      [OTP_ACTION.ACCEPT_AUTOINVEST]: 'aceptar_autoinversion',
      [OTP_ACTION.CHANGE_DATA]: 'cambio_datos',
   }

   sendOtp(action: OTP_ACTION) {
      return this.http.post<any>(`${environment.apiUrlBase}/parts/otp`, { action }, HEADER_DONT_SAVE).pipe(map(r => {
         this.toastr.info(`Se ha enviado un código temporal por ${this.otpMethod === 'sms' ? 'SMS' : 'email'} para validar su usuario.`);
         return r;
      }));
   }

   sendEmailOTPs(newEmail: string) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/edit/otp/email`, { newEmail }, HEADER_DONT_SAVE).pipe(map(r => {
         this.toastr.info(`Se han enviado los codigos temporales para validar su cambio de correo.`);
         return r;
      }));
   }

   createSpoOut(otp: string, amount: number, bankAccount: string, mfa?: boolean): Observable<{ id: string }> {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', this.utils.utf8ToBase64(otp));
      const service = mfa ? '/wallet/mfa/create-spo' : '/wallet/otp/create-spo';

      return this.gpsService.getOrRequestCoordinates().pipe(
        switchMap(({ latitude, longitude }) => {
          const body = { bankAccount, amount, latitude, longitude };
          const url = `${environment.apiUrlBase}${service}`;
          return this.http.post<{ id: string }>(url, body, { headers });
        }),
        map(res => {
          this.cleanUserCache();
          return res;
        }),
        catchError(err => {
          return throwError(() => new Error('No fue posible procesar el retiro.'));
        })
      );
    }


   /** Función para almacenar datos de domiciliación de inversión */
   createDirectDebit(infoToSend) {
      return this.http.post<{ dd: any }>(`${environment.apiUrlBase}/wallet/create-direct-debit`, infoToSend, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         map(res => {
            this.cleanUserCache();
            return res;
         })
      )
   }

   cleanUserCache(onlyUser?: boolean) {
      onlyUser ? this.cacheUser.getUserInfo = {} : this.cacheUser = {};

      // Limpiar también el caché de getUserDetail
      this.clearUserDetailCache();

      // Resetear banderas de carga
      this.resetLoadingFlags();
   }

   /** Método para limpiar el caché de getUserDetail cuando sea necesario */
   clearUserDetailCache() {
      this.userDetailCacheData = null;
      this.userDetailCacheTime = 0;
      this.userDetailInProgress = null;
   }

   /** Método para resetear todas las banderas de carga */
   private resetLoadingFlags() {
      this.userOnLoad = false;
      this.userInfoAdditionalOnLoad = false;
      this.userSettingsOnLoad = false;
      this.bankAccountsOnLoad = false;
      this.referenceOnLoad = false;
      this.loadUserInProgress = null;
   }

   addBeneficiary(benef: { name: string; curp: string; percent: number; address: string }, options: ValidationType) {

      const { isNativePlatform, actualMethod, code } = options;

      let endpoint = '/user/edit/beneficiary';
      if (!isNativePlatform) {
        endpoint += actualMethod === 'auth-app' ? '/mfa' : '/otp';
      }

      let headers = HEADER_DONT_SAVE_REQ;
      if (!isNativePlatform && code) {
         const encodedCode = actualMethod === 'auth-app' ? window.btoa(code) : this.utils.encodeBase64(code);
         headers = headers.set('tk', encodedCode);
      }

      return this.http.post<UserBeneficiary>(`${environment.apiUrlBase}${endpoint}`,{ ...benef },{ headers }).pipe(tap(() => this.cleanUserCache()));
   }

   updateBeneficiary(id: string, percent: string) {
      return this.http.put<UserBeneficiary>(`${environment.apiUrlBase}/user/edit/beneficiary`, { percent, id }, HEADER_DONT_SAVE);
   }

   updateBeneficiaryNewMethod(id: string, name: string, curp: string, percent: number, address: string, options: ValidationType) {

      const { isNativePlatform, actualMethod, code } = options;

      let endpoint = '/user/edit/beneficiary';
      if (!isNativePlatform) {
        endpoint += actualMethod === 'auth-app' ? '/mfa' : '/otp';
      }

      let headers = HEADER_DONT_SAVE_REQ;
      if (!isNativePlatform && code) {
         const encodedCode = actualMethod === 'auth-app' ? window.btoa(code) : this.utils.encodeBase64(code);
         headers = headers.set('tk', encodedCode);
      }

      return this.http.put<UserBeneficiary>(`${environment.apiUrlBase}${endpoint}`,{ id, name, curp, percent, address },{ headers }).pipe(tap(() => this.cleanUserCache()));
   }

   deleteBeneficiary(id: string, options: ValidationType) {
      const { isNativePlatform, actualMethod, code } = options;

      let endpoint = '/user/edit/beneficiary';
      if (!isNativePlatform) {
        endpoint += actualMethod === 'auth-app' ? '/mfa' : '/otp';
      }

      let headers = HEADER_DONT_SAVE_REQ;
      if (!isNativePlatform && code) {
         const encodedCode = actualMethod === 'auth-app' ? window.btoa(code) : this.utils.encodeBase64(code);
         headers = headers.set('tk', encodedCode);
      }

      return this.http.delete<any>(`${environment.apiUrlBase}${endpoint}`, { body: { id }, headers }).pipe(tap(() => this.cleanUserCache()));
    }

   validateCURP(curp: string) {
      return this.http.put<any>(`${environment.apiUrlBase}/user/edit/validate-curp-beneficiary`, { curp }, HEADER_DONT_SAVE);
   }

   getOnSaleActive(devId: string,) {
      return this.http.post<{ amount: number; createAt: number; sold: number; id: string }[]>(
         `${environment.apiUrlBase}/resale/get-on-sale`, { devId }, HEADER_DONT_SAVE);
   }

   getNIPStatus() {
      return this.http.get<{ status: boolean, terms: 1 | 0 }>(`${environment.apiUrlBase}/user/nip`, HEADER_DONT_SAVE);
   }

   setNIP(nip: string) {
      return this.http.post<{ status: boolean }>(`${environment.apiUrlBase}/user/nip`, { nip }, HEADER_DONT_SAVE);
   }

   reqResetNIP() {
      return this.http.post<{ status: boolean }>(`${environment.apiUrlBase}/user/req-reset-nip`, {}, HEADER_DONT_SAVE).pipe(tap(() => this.cleanUserCache()));
   }

   resetNIP(token: string) {
      return this.http.put<{ status: boolean }>(`${environment.apiUrlBase}/user/nip`, { token }, HEADER_DONT_SAVE);
   }

   /** Aceptar terminos y condiciones del mercado secundario */
   acceptResaleTermsV2(): Observable<any> {
      return this.gpsService.getOrRequestCoordinates().pipe(
        map(({ longitude, latitude, accuracy }) => {
          const params = this.utils.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
          return { url: `${environment.apiUrlBase}/user/accept-rs`, body: { params } };
        }),
        switchMap(({ url, body }) =>
          this.http.post<any>(url, body, { headers: HEADER_DONT_SAVE_REQ })
        ),
        catchError(err => {
          return throwError(() => new Error('No fue posible aceptar los términos de reventa.'));
        })
      );
   }

   cancelOnSaleActive(id: string,) {
      return this.http.post<{ amount: number; createAt: number; sold: number; }[]>(`${environment.apiUrlBase}/resale/cancel-on-sale`,
         { id }, HEADER_DONT_SAVE);
   }

   updateDistributionInvoiced(param: boolean) {
      return this.http.put<{ distributionInvoiced: boolean }>(`${environment.apiUrlBase}/user/edit/dist-invoiced`, { param }, HEADER_DONT_SAVE)
         .pipe(tap(() => this.cacheUser = {}));
   }

   /** Cambiar opción de facturar compras de mercado secundario */
   updateBuyingInvoice(param: boolean) {
      return this.http.put<{ distributionInvoiced: boolean }>(`${environment.apiUrlBase}/user/edit/buying-invoice`, { param }, HEADER_DONT_SAVE);
   }

   updateEmailPromotions(param: boolean) {
      return this.http.put<{ promotions: boolean }>(`${environment.apiUrlBase}/user/edit/mail-promotions`, { param }, HEADER_DONT_SAVE);
   }

   /** Obtener Blob PDF de contrato de usuario */
   getContractPDF(createAt?: number) {
      let headers = HEADER_DONT_SAVE_REQ;
      if(createAt != undefined || createAt != null) {
         headers = headers.set('createat', createAt?.toString());
      }
      return this.http.get(`${environment.apiUrlBase}/user/file/contract`, { responseType: 'arraybuffer', headers: headers });
   }

   /** Obtener certificados de participación */
   getCertificatesPDF(project: any, campaign: any, isNativePlatform: boolean) {
      let nativePlatform: string = isNativePlatform ? 'true' : 'false';
      let queryParamsToSend = `${project.devName}/${campaign.campaign}/${campaign.totalParts}/${project.company}/${nativePlatform}`;
      let headers = HEADER_DONT_SAVE_REQ;
      return this.http.get(`${environment.apiUrlBase}/user/file/certificate/${queryParamsToSend}`, { responseType: 'arraybuffer', headers: headers });
   }

   getContractPDFAutoInvest() {
      let headers = HEADER_DONT_SAVE_REQ;
      return this.http.get(`${environment.apiUrlBase}/user/file/contract-autoinvest`, { responseType: 'arraybuffer', headers: headers });
   }


   setTermsRisk(email: string, value: boolean) {
      return this.http.put<{ email: string, value: boolean }>(`${environment.apiUrlBase}/user/set-terms`, { email, value }, HEADER_DONT_SAVE);
   }

   sendMailPep(properties: any) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/send-email-pep`, properties, { headers: HEADER_DONT_SAVE_REQ });
   }

   sendMailDec(properties: any) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/send-email-dec`, properties, { headers: HEADER_DONT_SAVE_REQ });
   }

   updateRFC(rfc: string) {
      return this.http.post<{ curp: boolean, rfc: boolean }>(`${environment.apiUrlBase}/user/edit/rfc`, { rfc }, HEADER_DONT_SAVE);
   }

   updateRegStandar(data: { user, info, birthDate }) {
      return this.http.post<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/up-standar`, data, HEADER_DONT_SAVE);
   }

   getUserRegInfo() {
      return this.http.post<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/info`, {}, HEADER_DONT_SAVE);
   }

   updatePersonalInfo(user: any, info: any, address: any) {
      return this.http.post<UserRegisterIneRes>(`${environment.apiUrlBase}/user/edit/up-info`, { user, info, address }, HEADER_DONT_SAVE);
   }

   reqValidPhone(phone: string) {
      return this.http.post<{ sended: boolean }>(`${environment.apiUrlBase}/user/edit/valid-phone`, { phone }, HEADER_DONT_SAVE);
   }

   validPhone(otp: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', this.utils.utf8ToBase64(otp));
      return this.http.post<{ sended: boolean }>(`${environment.apiUrlBase}/user/edit/otp/phone`, {}, { headers });
   }

   updateOtpMethod(method: OTP_METHOD) {
      return this.http.put<OtpMethodRes>(`${environment.apiUrlBase}/user/edit/otp/chage-method`, { param: this.utils.utf8ToBase64(method) }, HEADER_DONT_SAVE)
         .pipe(tap(r => this.otpMethod = r.method));
   }

   generateAuthAppQR() {
      return this.http.post<{ message: string; tempSecret: string; dataURL: string; tfaURL: string; }>(`${environment.apiUrlBase}/user/edit/generate-auth`, {}, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   removeAuthApp() {
      return this.http.post<{ status: boolean }>(`${environment.apiUrlBase}/user/edit/remove-auth`, {}, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   cancelsubscription(mailPromotions: boolean) {
      return this.http.post<{ mailPromotions: boolean }>(`${environment.apiUrlBase}/user/cancel-subscription`, { mailPromotions }, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   validAuthAppQR(token: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', this.utils.utf8ToBase64(token));

      return this.http.post<{ status: boolean }>(`${environment.apiUrlBase}/user/edit/valid-auth`, {}, { headers })
         .pipe(tap(() => { delete this.cacheUser.getUserSettings }))
   }

   uploadDocumentsPLD(file: File, type: 'proofAddress', token?: string) {
      const data = new FormData();

      const headers = HEADER_DONT_SAVE_REQ.set('tk', token);

      data.set(type, file);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/user/up-user-doc-pld`, data, { headers });
   }

   upDocumentsPLDData(
      file: File,
      type: 'proofaddress' |
         'passport' |
         'idCardFront' |
         'idCardBack' |
         'passport' |
         'taxCertificate',
      token?: string
   ) {
      const data = new FormData();

      const headers = HEADER_DONT_SAVE_REQ.set('tk', token);

      data.set(type, file);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/user/up-docs-pld-info`, data, { headers });
   }

   /** New Function - Upload Signature*/
   uploadSignaturePLD(dataURL: string, lat?: number, lng?: number, accuracy?: number, token?: string) {
      const blobBin = this.utils.base64ToUtf8(dataURL.split(',')[1]);
      const array = [];
      let options = HEADER_DONT_SAVE_REQ;

      for (let i = 0; i < blobBin.length; i++) {
         array.push(blobBin.charCodeAt(i));
      }

      const blobSignature: any = new Blob([new Uint8Array(array)], { type: 'image/png' });

      //A Blob() is almost a File() - it's just missing the two properties below which we will add
      blobSignature.lastModifiedDate = new Date();
      blobSignature.name = 'signature';

      const signatureForm = new FormData();

      const params = this.utils.utf8ToBase64(`signature|${lng}|${lat}|${accuracy}`);

      signatureForm.set('file', blobSignature);
      signatureForm.set('p', params);

      const headers = options.set('tk', token);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/user/up-signature-pld`, signatureForm, { headers });
   }

   updatePLDQuestionnarie(user: any, reqUpdateToken?: string) {
      const config = { headers: reqUpdateToken ? HEADER_DONT_SAVE_REQ.set('tk', reqUpdateToken) : HEADER_DONT_SAVE_REQ };
      const service = reqUpdateToken ? 'user/pd/up-data-pld-quiz' : 'user/warning';

      return this.http.post(`${environment.apiUrlBase}/${service}`, { user }, config)
         .pipe(map(u => {
            this.cacheUser.getUserDetail = undefined;
            return u;
         }));
   }

   acceptAutoninvestContract(token: string, { minAmount, type }: { minAmount: number; type: string[] }): Observable<{ status: boolean }> {
      const encodedToken = this.utils.utf8ToBase64(token);

      return this.gpsService.getOrRequestCoordinates().pipe(
        map(({ latitude, longitude, accuracy }) => {
          const lct = this.utils.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);
          const headers = HEADER_RESET.headers.set('tk', lct);

          return {
            url: `${environment.apiUrlBase}/user/autoinvest/accept`,
            body: { pwd: encodedToken, lct, minAmount: String(minAmount), type },
            headers
          };
        }),
        switchMap(({ url, body, headers }) =>
          this.http.post<{ status: boolean }>(url, body, { headers })
        ),
        catchError(err => {
          this.toastr.warning('No fue posible obtener ubicación para firmar contrato.');
          return throwError(() => new Error('Error al procesar autoinversión: ' + err.message));
        })
      );
   }

   updateDirectDebitStatus(status: string) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/autoinvest/status-dd`, {status}, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   deleteDirectDebitStatus() {
      return this.http.post<any>(`${environment.apiUrlBase}/user/autoinvest/delete-dd`, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   /** Crear autoinversión desde contrato */
   createAutoInvestContract() {
      return this.http.post<any>(`${environment.apiUrlBase}/user/autoinvest/create`, {}, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   /** Obtener información sobre si el usuario ya activo la domicilicación */
   getInfoDomiciliation() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/autoinvest/info-domiciliation`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   updateAutoinvestStatus() {
      return this.http.post<any>(`${environment.apiUrlBase}/user/autoinvest/status`, {}, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   updateAutoInvest(data) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/edit/autoinvest`, data, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getInfoAutoInvest() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/autoinvest/info`, { headers:  HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getDevStep() {
      return this.http.post<{ regStep: number }>(`${environment.apiUrlBase}/user/get-dev-step`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   updateCFDIInfo(zipCode: string, cfdiUse: string, fiscalRegime: string, personType: string) {
      return this.http.post<{ status: string }>(`${environment.apiUrlBase}/user/edit/update-fiscal-info`, { zipCode, cfdiUse, fiscalRegime, personType }, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   checkDashNotifications() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/notifications`, HEADER_DONT_SAVE).pipe(
         take(1),
      );
   }

   modifyDomiciliation(body: any) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/autoinvest/modify`, body, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   // Firmar contrato de domiciliación
   signatureContractDomiciliation(body: {bankName: string;amountToRecollect: string;clabe: string;frecuency: string;totalMonths: number;typePlan: string }) {
      return this.gpsService.getOrRequestCoordinates().pipe(
        switchMap(coords => {
          if (!coords || coords.latitude == null || coords.longitude == null || coords.accuracy == null) {
            return throwError(() => new Error('Ubicación no disponible o incompleta'));
          }

          const { latitude, longitude, accuracy } = coords;

          const encodedLocation = this.utils.utf8ToBase64(`${latitude}|${longitude}|${accuracy}`);
          const headers = HEADER_DONT_SAVE_REQ.set('tk', encodedLocation);

          return this.http.post<{ url: any }>(
            `${environment.apiUrlBase}/user/edit/sign-contract-domiciliation`,
            body,
            { headers }
          );
        })
      );
   }

   /** Función para cambiar el teléfono y correo electrónico con OTP */
   updateEmailPhone(otp: string, body: {type: string, email?: string, phone?: string}) {
      // Definir tipo de header
      const headers = HEADER_DONT_SAVE.headers.set('tk', this.utils.encodeBase64(otp));

      // URL Base
      const baseUrl = `${environment.apiUrlBase}/user/edit`;

      return this.http.post<{ email?: string, phone?: string }>(`${baseUrl}/update-email-phone`, body, { headers: headers });

   }

   // Funcion para validar dos tokens temporales para cambiar el correo electrónico
   validateEmailChange(body: { tokenActualEmail: string, tokenNewEmail: string, newEmail: string, oldEmail: string }) {

      const tokensJoined = `${body.tokenActualEmail}|${body.tokenNewEmail}`;

      // Definir tipo de header
      let headers = HEADER_DONT_SAVE.headers.set('tk', this.utils.encodeBase64(tokensJoined));

      const bodyRequest = { newEmail: body.newEmail, oldEmail: body.oldEmail };

      return this.http.post<{ status: boolean }>(`${environment.apiUrlBase}/user/edit/otp/validate-email-change`, bodyRequest, { headers });
   }

   getTotalEarnings(): Observable<{ totalGenerado: number }>{
      return this.http.get<{ totalGenerado: number }>(`${environment.apiUrlBase}/user/earnings`, HEADER_DONT_SAVE);
   }

   /** Firmar contrato de forma digital */
   async signatureContract() {
     const coords = await this.gpsService.getCoordinatesFromLocalStorage();

     if (!coords) {
       console.log('Coordenadas GPS no disponibles.');
       throw new Error('Ubicación no disponible');
     }

     const { latitude, longitude, accuracy } = coords;
     const params = this.utils.utf8ToBase64(`${longitude}|${latitude}|${accuracy}`);

     return this.http.post<any>(
       `${environment.apiUrlBase}/user/sign-contract`,
       { params },
       { headers: HEADER_DONT_SAVE_REQ }
     )
   }

   /** Obtener contrato actualizado desde el dashboard */
   getContractPDFDashboard(createAt: number) {
      return this.http.post(`${environment.apiUrlBase}/user/contract-dashboard`, { createAt }, { responseType: 'arraybuffer', headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   /** Ultima actualización del contrato en general */
   getLastUpdateContract() {
      return this.http.get<{ lastUpdateContractMkt: number, lastUpdateContractDom: number, lastUpdateContract: number, lastUpdateContractAutoInvest: number }>(`${environment.apiUrlBase}/public/last-update-contract`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   /** Obtener ultima firma del contrato */
   getLastSignatureContract() {
      return this.http.get(`${environment.apiUrlBase}/user/last-signature-contract`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   /** Obtener cantidad de participaciones e documentos INE */
   getIDUser() {
      return this.http.get<{ idCardBack: string, idCardFront: string, otherDocument: string, totalParts: number, bloqWithDraw: number }>(`${environment.apiUrlBase}/user/info/id-parts`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   /** Guardar datos de analytics en la base de datos */
   saveAnalytics(paramsCampaign: any) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/analytics-campaign`, { paramsCampaign } , { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   /** Obtener todas las ganancias del usuario */
   getProfitDashboard() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/get-profit`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   pingUser(platform: string) {
      return this.http.post<any>(`${environment.apiUrlBase}/user/ping-session`, { platform }, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         catchError((err) => {
            return of(null);
         })
      );
   }

   getAllContracts() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/get-contracts`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getContractsActive() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/get-contracts-active`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getAllActiveParts() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/get-active-parts`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   investmentsActive() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/investments/active`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         timeout(30000), // 30 seconds timeout
         retry({ count: 2, delay: 1000 }),
         take(1),
         catchError(() => {
            return of([]);
         })
      );
   }

   initInternalReferral() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/init-referral-internal`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   investmentsInactive() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/investments/inactive`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         timeout(30000), // 30 seconds timeout
         retry({ count: 2, delay: 1000 }),
         take(1),
         catchError(() => {
            return of([]);
         })
      );
   }

   investmentsMktSolding() {
      return this.http.get<any>(`${environment.apiUrlBase}/user/info/investments/mkt-active`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getDistributionPerProjectForUser(devName: string){
      return this.http.post<any>(`${environment.apiUrlBase}/user/info/distribution-per-user`, { devName }, { headers: HEADER_DONT_SAVE_REQ }).pipe(map(res => { return res || [];}));
   }

   getOnSalePublished() {
      return this.http.get<UserPartsPublished>(`${environment.apiUrlBase}/user/info/get-onsale-published-parts`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         timeout(30000), // 30 seconds timeout
         retry({ count: 2, delay: 1000 }),
         take(1),
         catchError(() => {
            return of([]);
         })
      );
   }

   getOnSaleSolding() {
      return this.http.get<UserPartsSolding>(`${environment.apiUrlBase}/user/info/get-onsale-solding-parts`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         timeout(30000), // 30 seconds timeout
         retry({ count: 2, delay: 1000 }),
         take(1),
         catchError(() => {
            return of([]);
         })
      )
   }

   getDetailsInvestmentsPID(pid: string) {
      return this.http.get<DevelopmentPerProject>(`${environment.apiUrlBase}/user/info/details-investments/${pid}`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getAllInfoCalendarPayments() {
      return this.http.get<DevelopmentCalendarPayments>(`${environment.apiUrlBase}/user/info/calendar-payments`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getUniqueBalance() {
      return this.http.get<{ uniqueBalance: number }>(`${environment.apiUrlBase}}/user/info/wallet`, { headers: HEADER_DONT_SAVE_REQ }).pipe(
         take(1),
      );
   }

   getExistsFiscalInfo() {
      return this.http.get<{ hasFiscalInfo: boolean, buyingInvoice: boolean}>(`${environment.apiUrlBase}/user/info/get-fiscal-info`, { headers: HEADER_DONT_SAVE_REQ });
   }

   getFiscalInfoDetails() {
      return this.http.get<{ personType: string, cfdiUse: string, fiscalRegime: number, zipCode: string | number }>(`${environment.apiUrlBase}/user/info/get-fiscal-info-detail`, { headers: HEADER_DONT_SAVE_REQ })
   }

}
